import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useVehicles } from '@/hooks/useVehicles'
import { useReservations } from '@/hooks/useReservations'
import { useAuth } from '@/hooks/useAuth'
import { useDiscountRules } from '@/hooks/useDiscountRules'
import {Calendar, Users, Fuel, Settings, MapPin, Shield, ChevronLeft, Check, Clock, CreditCard, FileText, Sparkles, Sun, Sunset, ChevronRight, AlertTriangle, Percent, MessageCircleDashed as MessageCircle} from 'lucide-react'
import ImageGallery from './ImageGallery'
import AuthModal from './AuthModal'

type DurationType = 'demi-journee' | 'journee'
type HalfDaySlot = 'matin' | 'apres-midi'

export default function BookingPage() {
  // Scroll automatique en haut de page au chargement
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [])

  const [searchParams] = useSearchParams()
  const id = searchParams.get('vehicleId')
  const navigate = useNavigate()
  const { vehicles, loading, fetchVehicles } = useVehicles()
  const { createReservation, checkAvailability, fetchBookedDates } = useReservations()
  const { user, isAuthenticated } = useAuth()
  const { rules: discountRules, fetchRules: fetchDiscountRules, getApplicableDiscount } = useDiscountRules()

  const [vehicle, setVehicle] = useState<any>(null)
  const [pickupDate, setPickupDate] = useState('')
  const [duration, setDuration] = useState<DurationType>('journee')
  const [halfDaySlot, setHalfDaySlot] = useState<HalfDaySlot>('matin')
  const [numberOfDays, setNumberOfDays] = useState(1)
  const [clientName, setClientName] = useState('')
  const [clientEmail, setClientEmail] = useState('')
  const [clientPhone, setClientPhone] = useState('')
  const [clientAddress, setClientAddress] = useState('')
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [step] = useState(1)
  const [bookedDates, setBookedDates] = useState<string[]>([])
  const [loadingDates, setLoadingDates] = useState(false)
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date()
    return { year: now.getFullYear(), month: now.getMonth() }
  })
  const [plannedKm, setPlannedKm] = useState<number>(0)
  const [sundayConfirmModal, setSundayConfirmModal] = useState<{ open: boolean; targetDays: number }>({ open: false, targetDays: 0 })
  const [addDayToast, setAddDayToast] = useState<string>('')

  useEffect(() => {
    fetchVehicles()
    fetchDiscountRules()
  }, [])

  useEffect(() => {
    if (vehicles.length > 0 && id) {
      const found = vehicles.find((v) => v._id === id)
      setVehicle(found)
    }
  }, [vehicles, id])

  // Charger les dates reservees quand le vehicule est connu
  useEffect(() => {
    if (vehicle?._id) {
      setLoadingDates(true)
      fetchBookedDates(vehicle._id).then((dates) => {
        setBookedDates(dates)
        setLoadingDates(false)
      })
    }
  }, [vehicle?._id])

  // Utilitaires calendrier
  const bookedDatesSet = useMemo(() => new Set(bookedDates), [bookedDates])

  const isDateBooked = (dateStr: string) => bookedDatesSet.has(dateStr)

  const isDateRangeBooked = (startStr: string, days: number) => {
    const start = new Date(startStr)
    for (let i = 0; i < days; i++) {
      const d = new Date(start)
      d.setDate(d.getDate() + i)
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      if (bookedDatesSet.has(`${y}-${m}-${dd}`)) return true
    }
    return false
  }

  const isDatePast = (dateStr: string) => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const d = new Date(dateStr)
    d.setHours(0, 0, 0, 0)
    return d < today
  }

  // Le garage est ferme le dimanche : pas de prise ni de retour possible ce jour-la
  const isSunday = (dateStr: string) => {
    if (!dateStr) return false
    const d = new Date(dateStr + 'T00:00:00')
    return d.getDay() === 0
  }

  // Aujourd'hui : si l'heure actuelle a depasse le dernier creneau de debut (13h00 apres-midi),
  // plus aucune periode n'est reservable en ligne pour aujourd'hui -> bloquer la date dans le calendrier.
  const areAllTodayPeriodsPassed = () => {
    const now = new Date()
    // Dernier creneau de debut = 13h00 (apres-midi). Si on est >= 13h00, toutes les periodes sont depassees.
    return now.getHours() >= 13
  }

  const isTodayFullyPassed = (dateStr: string) => {
    if (!dateStr) return false
    // Comparer en tant que chaîne YYYY-MM-DD pour eviter tout probleme de fuseau horaire
    const now = new Date()
    const y = now.getFullYear()
    const m = String(now.getMonth() + 1).padStart(2, '0')
    const d = String(now.getDate()).padStart(2, '0')
    const todayStr = `${y}-${m}-${d}`
    if (dateStr !== todayStr) return false
    return areAllTodayPeriodsPassed()
  }

  const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate()
  const getFirstDayOfMonth = (year: number, month: number) => {
    const day = new Date(year, month, 1).getDay()
    return day === 0 ? 6 : day - 1 // Lundi = 0
  }

  const calendarDays = useMemo(() => {
    const { year, month } = calendarMonth
    const daysInMonth = getDaysInMonth(year, month)
    const firstDay = getFirstDayOfMonth(year, month)
    const days: { date: string; day: number; isBooked: boolean; isPast: boolean; isSunday: boolean; isToday: boolean; isSelected: boolean; inRange: boolean }[] = []

    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`

    for (let i = 0; i < firstDay; i++) {
      days.push({ date: '', day: 0, isBooked: false, isPast: true, isSunday: false, isToday: false, isSelected: false, inRange: false })
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
      // Si c'est aujourd'hui mais que toutes les periodes sont passees, on le considere comme "passe"
      const past = isDatePast(dateStr) || isTodayFullyPassed(dateStr)
      const booked = isDateBooked(dateStr)
      const sunday = isSunday(dateStr)
      const selected = pickupDate === dateStr

      // Calculer si ce jour fait partie de la plage selectionnee
      let inRange = false
      if (pickupDate && duration === 'journee' && numberOfDays > 1) {
        const startD = new Date(pickupDate)
        const endD = new Date(pickupDate)
        endD.setDate(endD.getDate() + numberOfDays - 1)
        const currentD = new Date(dateStr)
        inRange = currentD >= startD && currentD <= endD
      }

      days.push({
        date: dateStr,
        day: d,
        isBooked: booked,
        isPast: past,
        isSunday: sunday,
        isToday: dateStr === todayStr,
        isSelected: selected,
        inRange,
      })
    }
    return days
  }, [calendarMonth, bookedDatesSet, pickupDate, duration, numberOfDays])

  const prevMonth = () => {
    setCalendarMonth((prev) => {
      if (prev.month === 0) return { year: prev.year - 1, month: 11 }
      return { ...prev, month: prev.month - 1 }
    })
  }

  const nextMonth = () => {
    setCalendarMonth((prev) => {
      if (prev.month === 11) return { year: prev.year + 1, month: 0 }
      return { ...prev, month: prev.month + 1 }
    })
  }

  const monthNames = ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre']
  const dayNames = ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di']

  const handleDateSelect = (dateStr: string) => {
    if (!dateStr) return
    if (isDatePast(dateStr)) return
    if (isDateBooked(dateStr)) return
    // Si aujourd'hui et que toutes les periodes sont passees, on bloque la selection
    if (isTodayFullyPassed(dateStr)) {
      setError('Toutes les periodes de reservation pour aujourd\'hui sont deja passees (dernier debut possible : 13h00). Veuillez choisir un autre jour ou contacter le garage via WhatsApp.')
      return
    }
    // Le garage est ferme le dimanche : pas de prise de vehicule possible
    if (isSunday(dateStr)) {
      setError('Le garage est ferme le dimanche. La prise du vehicule n\u2019est pas possible ce jour-la (mais le dimanche est compte comme jour payant en cours de location).')
      return
    }
    setPickupDate(dateStr)
    setError('')
  }

  // Verifier si la plage selectionnee chevauche des dates reservees
  const hasConflict = useMemo(() => {
    if (!pickupDate) return false
    if (duration === 'demi-journee') return isDateBooked(pickupDate)
    return isDateRangeBooked(pickupDate, numberOfDays)
  }, [pickupDate, duration, numberOfDays, bookedDatesSet])

  // Verifier si l'ajout d'un jour supplementaire entrerait en conflit
  // (la journee supplementaire = jour numberOfDays + 1, soit index numberOfDays depuis le debut)
  const wouldNextDayConflict = useMemo(() => {
    if (!pickupDate || duration !== 'journee') return false
    const start = new Date(pickupDate + 'T00:00:00')
    // Verifier le prochain jour a ajouter, et si celui-ci est un dimanche, le jour d'apres (lundi)
    // puisque l'ajout +1 qui tombe un dimanche deviendra +2 (dimanche + lundi)
    const nextDay = new Date(start)
    nextDay.setDate(nextDay.getDate() + numberOfDays)
    const formatDate = (d: Date) => {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${dd}`
    }
    // Si le prochain jour est un dimanche, on devra aussi reserver le lundi suivant (+2 jours au total)
    if (nextDay.getDay() === 0) {
      const mondayAfter = new Date(nextDay)
      mondayAfter.setDate(mondayAfter.getDate() + 1)
      return bookedDatesSet.has(formatDate(nextDay)) || bookedDatesSet.has(formatDate(mondayAfter))
    }
    return bookedDatesSet.has(formatDate(nextDay))
  }, [pickupDate, duration, numberOfDays, bookedDatesSet])

  useEffect(() => {
    if (user) {
      setClientName(user.userName || '')
      setClientEmail(user.email || '')
    }
  }, [user])

  // Calcul des dates/heures de prise et retour
  const getPickupDateTime = () => {
    if (!pickupDate) return null
    if (duration === 'demi-journee' && halfDaySlot === 'apres-midi') {
      return new Date(`${pickupDate}T13:00:00`)
    }
    return new Date(`${pickupDate}T08:00:00`)
  }

  // Si le retour tombe un dimanche, on decale au lundi suivant (sans surcout)
  const adjustReturnIfSunday = (returnDate: Date): Date => {
    if (returnDate.getDay() === 0) {
      const adjusted = new Date(returnDate)
      adjusted.setDate(adjusted.getDate() + 1)
      return adjusted
    }
    return returnDate
  }

  const getReturnDateTime = () => {
    if (!pickupDate) return null
    if (duration === 'demi-journee') {
      if (halfDaySlot === 'matin') {
        return adjustReturnIfSunday(new Date(`${pickupDate}T12:00:00`))
      } else {
        return adjustReturnIfSunday(new Date(`${pickupDate}T17:00:00`))
      }
    }
    // Journee(s) complete(s) : retour a 17h le dernier jour
    const returnDate = new Date(pickupDate)
    returnDate.setDate(returnDate.getDate() + numberOfDays - 1)
    const year = returnDate.getFullYear()
    const month = String(returnDate.getMonth() + 1).padStart(2, '0')
    const day = String(returnDate.getDate()).padStart(2, '0')
    return adjustReturnIfSunday(new Date(`${year}-${month}-${day}T17:00:00`))
  }

  // Regle metier: interdire reservation samedi apres-midi si l'on est deja samedi apres 11h00
  const isSaturdayAfternoonBlocked = useMemo(() => {
    if (!pickupDate) return false
    if (duration !== 'demi-journee' || halfDaySlot !== 'apres-midi') return false
    const selected = new Date(pickupDate + 'T00:00:00')
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    // Uniquement si la date selectionnee est aujourd'hui ET que c'est un samedi
    if (selected.getTime() !== today.getTime()) return false
    if (selected.getDay() !== 6) return false
    // Bloque si il est 11h00 ou plus
    return now.getHours() >= 11
  }, [pickupDate, duration, halfDaySlot])

  // Regle metier: pour une reservation du jour meme, on ne peut reserver que les periodes
  // dont l'heure de debut n'est PAS ENCORE ENTAMEE. Si la periode a deja demarre, la reservation
  // en ligne est bloquee et un bouton WhatsApp permet de contacter le garage pour demander si
  // la prise du vehicule est encore possible.
  const periodStartPassed = useMemo(() => {
    if (!pickupDate) return false
    const selected = new Date(pickupDate + 'T00:00:00')
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    // Uniquement si la date selectionnee est aujourd'hui
    if (selected.getTime() !== today.getTime()) return false

    // Heure de debut du creneau choisi
    let startHour: number
    const startMinute = 0
    if (duration === 'demi-journee' && halfDaySlot === 'apres-midi') {
      startHour = 13
    } else {
      // Matin (8h00) ou journee complete (8h00)
      startHour = 8
    }

    const currentMinutes = now.getHours() * 60 + now.getMinutes()
    const startMinutes = startHour * 60 + startMinute
    // La periode est "entamee" si l'heure actuelle est >= heure de debut
    return currentMinutes >= startMinutes
  }, [pickupDate, duration, halfDaySlot])

  // Alerte dedicatee : la date selectionnee est un dimanche (pas de prise/retour possible)
  const isPickupSunday = useMemo(() => isSunday(pickupDate), [pickupDate])

  // Construction du message WhatsApp et du lien direct
  const whatsappNumber = '41795551234' // Numero du garage au format international sans +
  // Gestion du clic sur le bouton + : si le nouveau dernier jour tombe un dimanche,
  // on ouvre une modal pour proposer l'ajout de 2 jours (dimanche + lundi) car le retour
  // sera reporte au lundi. Sinon, on ajoute simplement 1 jour.
  const handleIncrementDays = () => {
    // Règle : on ne peut pas ajouter de jour tant qu'aucune date de prise en charge n'est sélectionnée
    if (!pickupDate) {
      setError('Veuillez d\'abord selectionner une date de prise en charge avant d\'ajouter des journees.')
      return
    }
    // Règle : on ne peut pas ajouter de jour si cela entre en conflit avec une reservation existante
    // On affiche un toast ephemere UNIQUEMENT au moment du clic, pour eviter de creer de la confusion
    // avec une selection actuelle parfaitement valide.
    if (wouldNextDayConflict) {
      setAddDayToast('Impossible d\'ajouter un jour : la journee suivante est deja reservee par un autre client.')
      window.setTimeout(() => setAddDayToast(''), 4000)
      return
    }
    if (duration !== 'journee') {
      setNumberOfDays(numberOfDays + 1)
      return
    }
    // Calcul du dernier jour si on ajoutait +1
    const start = new Date(pickupDate + 'T00:00:00')
    const newLastDay = new Date(start)
    newLastDay.setDate(newLastDay.getDate() + numberOfDays) // index numberOfDays = jour numberOfDays+1
    if (newLastDay.getDay() === 0) {
      // Dimanche detecte : on propose a l'utilisateur d'ajouter 2 jours (dimanche + lundi)
      setSundayConfirmModal({ open: true, targetDays: numberOfDays + 2 })
      return
    }
    setNumberOfDays(numberOfDays + 1)
  }

  const whatsappLink = useMemo(() => {
    const dateLabel = pickupDate
      ? new Date(pickupDate + 'T00:00:00').toLocaleDateString('fr-CH', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
      : 'aujourd\u2019hui'
    let periodLabel = 'journee complete (8h00 - 17h00)'
    if (duration === 'demi-journee') {
      periodLabel = halfDaySlot === 'matin' ? 'demi-journee matin (8h00 - 12h00)' : 'demi-journee apres-midi (13h00 - 17h00)'
    } else if (numberOfDays > 1) {
      periodLabel = `${numberOfDays} journees (8h00 - 17h00)`
    }
    const vehicleLabel = vehicle ? `${vehicle.brand || ''} ${vehicle.model || ''} (${vehicle.name || ''})`.trim() : 'un vehicule'
    const now = new Date()
    const currentTime = now.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' })
    const msg = `Bonjour Ady Location,\n\nJe souhaite reserver ${vehicleLabel} pour le ${dateLabel} en ${periodLabel}.\n\nLa periode a deja demarre (il est ${currentTime}), est-il encore possible de prendre le vehicule aujourd'hui ?\n\nMerci !`
    return `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(msg)}`
  }, [pickupDate, duration, halfDaySlot, numberOfDays, vehicle])

  const formatDateTime = (date: Date | null) => {
    if (!date) return '-'
    return date.toLocaleDateString('fr-CH', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }) + ' a ' + date.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' })
  }

  const getDurationLabel = () => {
    if (duration === 'demi-journee') {
      return halfDaySlot === 'matin' ? '1 demi-journee matin (8h - 12h)' : '1 demi-journee apres-midi (13h - 17h)'
    }
    if (numberOfDays === 1) return '1 journee (8h - 17h)'
    return `${numberOfDays} journees (8h - 17h)`
  }

  // Calcul des dimanches inclus dans la période ET du lundi supplémentaire si le retour tombe un dimanche.
  // RÈGLE MÉTIER :
  //  - Chaque dimanche DANS la période = jour payant (le véhicule reste chez le client).
  //  - Si le dernier jour (jour de retour théorique) tombe un dimanche, le retour est reporté au lundi.
  //    Le dimanche reste compté ET le lundi devient un jour payant SUPPLÉMENTAIRE
  //    (car le client conserve le véhicule 1 jour calendaire de plus).
  const sundayInfo = useMemo(() => {
    if (!pickupDate || duration !== 'journee' || numberOfDays < 1) {
      return { sundaysIncluded: 0, extraMondayDay: 0 }
    }
    let count = 0
    const start = new Date(pickupDate + 'T00:00:00')
    // Parcourt les jours DANS la période choisie (du jour 1 au jour numberOfDays)
    for (let i = 0; i < numberOfDays; i++) {
      const d = new Date(start)
      d.setDate(d.getDate() + i)
      if (d.getDay() === 0) count++
    }
    // Détecter si le dernier jour théorique tombe un dimanche → lundi de retour = +1 jour payant
    const lastDay = new Date(start)
    lastDay.setDate(lastDay.getDate() + numberOfDays - 1)
    const extraMondayDay = lastDay.getDay() === 0 ? 1 : 0
    return { sundaysIncluded: count, extraMondayDay }
  }, [pickupDate, duration, numberOfDays])

  const sundaysInPeriod = sundayInfo.sundaysIncluded
  const extraMondayDay = sundayInfo.extraMondayDay

  // Calcul dynamique du coût — se recalcule automatiquement à chaque changement
  const priceInfo = useMemo(() => {
    const empty = { basePrice: 0, discount: null as any, discountAmount: 0, finalPrice: 0, priceAfterDiscount: 0, unitPrice: 0, unitLabel: '', quantity: 0, weeks: 0, remainingDays: 0, weekPrice: 0, dayPrice: 0, boostedPercent: 0, baseKmPerDay: 0, bonusKmPerDay: 0, totalKmPerDay: 0, totalKmIncluded: 0, plannedKm: 0, extraKm: 0, extraKmPrice: 0, extraKmCost: 0, sundaysIncluded: 0, billedDays: 0 }
    if (!vehicle) return empty

    let basePrice = 0
    let unitPrice = 0
    let unitLabel = ''
    let quantity = 0
    let weeks = 0
    let remainingDays = 0
    const weekPrice = vehicle.pricePerWeek || (vehicle.pricePerDay || 0) * 7
    const dayPrice = vehicle.pricePerDay || 0

    // Le nombre de jours effectivement facturés inclut :
    //  - tous les jours calendaires sélectionnés (`numberOfDays`), dimanches compris ;
    //  - +1 jour si le retour théorique tombe un dimanche (reporté au lundi = jour supplémentaire facturable).
    const billedDays = duration === 'journee' ? numberOfDays + extraMondayDay : 0

    if (duration === 'demi-journee') {
      unitPrice = vehicle.pricePerHalfDay || Math.round((vehicle.pricePerDay || 0) / 2)
      unitLabel = 'demi-journée'
      quantity = 1
      basePrice = unitPrice
    } else {
      if (billedDays >= 7) {
        weeks = Math.floor(billedDays / 7)
        remainingDays = billedDays % 7
        basePrice = weeks * weekPrice + remainingDays * dayPrice
        unitPrice = dayPrice
        unitLabel = 'journée'
        quantity = billedDays
      } else {
        unitPrice = dayPrice
        unitLabel = 'journée'
        quantity = billedDays
        basePrice = billedDays * dayPrice
      }
    }

    // Appliquer le rabais en fonction du nombre de jours (valeur exacte configurée dans l'admin)
    const effectiveDays = duration === 'demi-journee' ? 0.5 : billedDays
    const discount = getApplicableDiscount(effectiveDays)
    let discountAmount = 0
    let boostedPercent = 0
    if (discount) {
      boostedPercent = discount.discountPercent
      discountAmount = Math.round(basePrice * boostedPercent / 100)
    }

    // Bonus km lié au rabais : même pourcentage que le rabais (ex: rabais 5% → +5% de km inclus par jour)
    const baseKmPerDay = vehicle.includedKm || 0
    const bonusKmPerDay = discount && baseKmPerDay > 0
      ? Math.round(baseKmPerDay * (boostedPercent / 100))
      : 0
    const totalKmPerDay = baseKmPerDay + bonusKmPerDay
    const totalDaysForKm = duration === 'demi-journee' ? 1 : billedDays
    const totalKmIncluded = totalKmPerDay * totalDaysForKm

    // Simulateur km : calcul des km supplémentaires et de leur coût
    const extraKmPrice = vehicle.extraKmPrice || 0
    const extraKm = Math.max(0, plannedKm - totalKmIncluded)
    const extraKmCost = Math.round(extraKm * extraKmPrice * 100) / 100
    const priceAfterDiscount = basePrice - discountAmount
    const finalPrice = Math.round((priceAfterDiscount + extraKmCost) * 100) / 100

    return {
      basePrice: Math.round(basePrice * 100) / 100,
      discount,
      discountAmount,
      finalPrice,
      priceAfterDiscount: Math.round(priceAfterDiscount * 100) / 100,
      unitPrice,
      unitLabel,
      quantity,
      weeks,
      remainingDays,
      weekPrice,
      dayPrice,
      boostedPercent,
      baseKmPerDay,
      bonusKmPerDay,
      totalKmPerDay,
      totalKmIncluded,
      plannedKm,
      extraKm,
      extraKmPrice,
      extraKmCost,
      sundaysIncluded: sundaysInPeriod,
      billedDays,
    }
  }, [vehicle, duration, halfDaySlot, numberOfDays, discountRules, getApplicableDiscount, plannedKm, sundaysInPeriod, extraMondayDay])

  const getMinDate = () => {
    const today = new Date()
    const year = today.getFullYear()
    const month = String(today.getMonth() + 1).padStart(2, '0')
    const day = String(today.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!isAuthenticated) {
      setShowAuthModal(true)
      return
    }

    if (!pickupDate) {
      setError('Veuillez selectionner une date de prise en charge')
      return
    }

    if (isPickupSunday) {
      setError('Le garage est ferme le dimanche : la prise du vehicule n\'est pas possible ce jour-la. Veuillez choisir une autre date.')
      return
    }

    if (isSaturdayAfternoonBlocked) {
      setError('Reservation du samedi apres-midi impossible apres 11h00 le jour meme. Veuillez choisir une autre date ou contacter le garage.')
      return
    }

    if (periodStartPassed) {
      setError('La periode de location a deja demarre. Contactez directement le garage via WhatsApp pour savoir si la prise du vehicule est encore possible aujourd\'hui.')
      return
    }

    const pickup = getPickupDateTime()
    const returnDt = getReturnDateTime()

    if (!pickup || !returnDt) {
      setError('Erreur dans le calcul des dates')
      return
    }

    setError('')
    setSubmitting(true)

    try {
      console.log('Starting reservation process...')

      const startISO = pickup.toISOString()
      const endISO = returnDt.toISOString()

      const isAvailable = await checkAvailability(vehicle._id, startISO, endISO)
      console.log('Availability check result:', isAvailable)

      if (!isAvailable) {
        setError('Ce vehicule n\'est pas disponible pour ces dates')
        setSubmitting(false)
        return
      }

      const reservationData = {
        vehicleId: vehicle._id,
        vehicleName: `${vehicle.name} - ${vehicle.brand} ${vehicle.model}`,
        clientName,
        clientEmail,
        clientPhone,
        clientAddress,
        startDate: startISO,
        endDate: endISO,
        durationType: duration,
        halfDaySlot: duration === 'demi-journee' ? halfDaySlot : undefined,
        numberOfDays: duration === 'demi-journee' ? 0.5 : numberOfDays,
        basePrice: vehicle.pricePerDay,
        optionsPrice: 0,
        totalPrice: priceInfo.finalPrice,
        options: [],
        status: 'pending',
        paymentStatus: 'unpaid',
        deposit: vehicle.deposit,
        depositStatus: 'pending',
        pickupLocation: 'Ady Garage - Bussigny',
        returnLocation: 'Ady Garage - Bussigny',
        reservationNumber: `ADY-${Date.now()}`,
        creator: user?.userId || 'guest',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }

      console.log('Creating reservation with data:', reservationData)

      const result = await createReservation(reservationData)
      console.log('Reservation created successfully:', result)

      alert('Reservation creee avec succes ! Vous recevrez une confirmation par email.')
      navigate('/mes-reservations')
    } catch (err: any) {
      console.error('Reservation creation failed:', err)
      setError(err.message || 'Erreur lors de la creation de la reservation')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Chargement...</div>
      </div>
    )
  }

  if (!vehicle && !loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl font-bold text-red-600 mb-4">Vehicule non trouve</div>
          <p className="text-gray-600 mb-6">Ce vehicule n'existe pas ou n'est plus disponible.</p>
          <button
            onClick={() => navigate('/flotte')}
            className="bg-yellow-400 text-black px-6 py-3 rounded-xl font-bold hover:bg-yellow-500 transition-colors"
          >
            Retour a la flotte
          </button>
        </div>
      </div>
    )
  }

  const isVehicleUnavailable = vehicle && ['indisponible', 'unavailable', 'maintenance'].includes(vehicle.status?.toLowerCase())
  const isVehicleHidden = vehicle && vehicle.status?.toLowerCase() === 'cache'

  if (isVehicleHidden) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-2xl font-bold text-red-600 mb-4">Vehicule non disponible</div>
          <p className="text-gray-600 mb-6">Ce vehicule n'est pas disponible actuellement.</p>
          <button
            onClick={() => navigate('/flotte')}
            className="bg-yellow-400 text-black px-6 py-3 rounded-xl font-bold hover:bg-yellow-500 transition-colors"
          >
            Voir les vehicules disponibles
          </button>
        </div>
      </div>
    )
  }

  const totalPrice = priceInfo.finalPrice
  const pickupDt = getPickupDateTime()
  const returnDt = getReturnDateTime()

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-yellow-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-black via-gray-900 to-black text-white py-8 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/flotte')}
            className="flex items-center gap-2 text-yellow-400 hover:text-yellow-300 mb-4 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            Retour a la flotte
          </button>
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Finalisez votre reservation</h1>
          <p className="text-gray-300">Quelques etapes simples pour partir avec votre vehicule</p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="bg-white shadow-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between max-w-3xl mx-auto">
            {[
              { num: 1, label: 'Vehicule', icon: Calendar },
              { num: 2, label: 'Coordonnees', icon: FileText },
              { num: 3, label: 'Confirmation', icon: CreditCard },
            ].map((s, idx) => {
              const Icon = s.icon
              const isActive = step >= s.num
              return (
                <div key={s.num} className="flex items-center flex-1">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center font-bold transition-all duration-300 ${
                        isActive
                          ? 'bg-gradient-to-br from-yellow-400 to-yellow-500 text-black shadow-lg scale-110'
                          : 'bg-gray-200 text-gray-400'
                      }`}
                    >
                      <Icon className="w-6 h-6" />
                    </div>
                    <span className={`text-xs mt-2 font-medium ${
                      isActive ? 'text-black' : 'text-gray-400'
                    }`}>
                      {s.label}
                    </span>
                  </div>
                  {idx < 2 && (
                    <div className={`h-1 flex-1 mx-2 rounded transition-all duration-300 ${
                      step > s.num ? 'bg-gradient-to-r from-yellow-400 to-yellow-500' : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left: Vehicle Details */}
          <div>
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden mb-6 border-2 border-yellow-400/20 hover:border-yellow-400/40 transition-all">
              <div className="relative">
                <ImageGallery
                  mainImage={vehicle.imageUrl}
                  images={vehicle.images}
                  alt={vehicle.name}
                  height="h-72"
                  showThumbnails={true}
                  vehicleId={vehicle._id}
                  showCounter={true}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
                <div className="absolute bottom-4 left-4 right-4 pointer-events-none">
                  <div className="bg-yellow-400 text-black px-3 py-1 rounded-full text-sm font-bold inline-block mb-2">
                    {vehicle.category}
                  </div>
                </div>
              </div>
              <div className="p-6 bg-gradient-to-br from-white to-gray-50">
                <h1 className="text-3xl font-bold bg-gradient-to-r from-black to-gray-700 bg-clip-text text-transparent mb-2">{vehicle.name}</h1>
                <p className="text-xl text-gray-600 mb-6 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-yellow-500" />
                  {vehicle.brand} {vehicle.model}
                </p>

                <div className="grid grid-cols-2 gap-3 mb-6">
                  <div className="flex items-center gap-3 bg-white p-3 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="bg-gradient-to-br from-yellow-400 to-yellow-500 p-2 rounded-lg">
                      <Users className="w-5 h-5 text-black" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Places</div>
                      <div className="font-semibold text-black">{vehicle.seats}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 bg-white p-3 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="bg-gradient-to-br from-gray-700 to-gray-900 p-2 rounded-lg">
                      <Settings className="w-5 h-5 text-yellow-400" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Boite</div>
                      <div className="font-semibold text-black">{vehicle.transmission}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 bg-white p-3 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="bg-gradient-to-br from-green-400 to-green-600 p-2 rounded-lg">
                      <Fuel className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Carburant</div>
                      <div className="font-semibold text-black">{vehicle.fuel}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 bg-white p-3 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="bg-gradient-to-br from-blue-400 to-blue-600 p-2 rounded-lg">
                      <Shield className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Caution</div>
                      <div className="font-semibold text-black">CHF {vehicle.deposit}</div>
                    </div>
                  </div>
                </div>

                {/* Horaires info */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200 mb-4">
                  <h3 className="font-bold text-black mb-3 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-blue-600" />
                    Horaires de location
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-3 bg-white p-3 rounded-lg shadow-sm">
                      <Sun className="w-5 h-5 text-yellow-500" />
                      <div>
                        <div className="text-xs text-gray-500">Prise du vehicule</div>
                        <div className="font-bold text-black">{duration === 'demi-journee' && halfDaySlot === 'apres-midi' ? '13h00' : '08h00'}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 bg-white p-3 rounded-lg shadow-sm">
                      <Sunset className="w-5 h-5 text-orange-500" />
                      <div>
                        <div className="text-xs text-gray-500">Retour du vehicule</div>
                        <div className="font-bold text-black">{duration === 'demi-journee' && halfDaySlot === 'matin' ? '12h00' : '17h00'}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl p-4 border border-yellow-200">
                  <h3 className="font-bold text-black mb-3 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-yellow-500" />
                    Caracteristiques incluses
                  </h3>
                  <ul className="grid grid-cols-2 gap-3">
                    {vehicle.features?.map((feature: string, idx: number) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-700 bg-white px-3 py-2 rounded-lg shadow-sm">
                        <Check className="w-4 h-4 text-green-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Calcul du coût total */}
                <div className="mt-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200">
                  <h3 className="font-bold text-black mb-3 flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-gray-600" />
                    Calcul du coût total
                  </h3>
                  {!vehicle ? (
                    <div className="bg-white px-4 py-4 rounded-lg text-center text-sm text-gray-500 border border-dashed border-gray-300">
                      Chargement du véhicule...
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {/* Détail du calcul de base */}
                      {duration === 'demi-journee' ? (
                        <div className="flex justify-between items-center bg-white px-4 py-2 rounded-lg">
                          <span className="text-gray-600 text-sm">1 × demi-journée</span>
                          <span className="font-bold text-black">CHF {priceInfo.unitPrice}</span>
                        </div>
                      ) : priceInfo.weeks > 0 ? (
                        <>
                          <div className="flex justify-between items-center bg-white px-4 py-2 rounded-lg">
                            <span className="text-gray-600 text-sm">{priceInfo.weeks} × semaine (7j) × CHF {priceInfo.weekPrice}</span>
                            <span className="font-bold text-black">CHF {priceInfo.weeks * priceInfo.weekPrice}</span>
                          </div>
                          {priceInfo.remainingDays > 0 && (
                            <div className="flex justify-between items-center bg-white px-4 py-2 rounded-lg">
                              <span className="text-gray-600 text-sm">{priceInfo.remainingDays} × journée × CHF {priceInfo.dayPrice}</span>
                              <span className="font-bold text-black">CHF {priceInfo.remainingDays * priceInfo.dayPrice}</span>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="flex justify-between items-center bg-white px-4 py-2 rounded-lg">
                          <span className="text-gray-600 text-sm">{priceInfo.quantity} × journée × CHF {priceInfo.unitPrice}</span>
                          <span className="font-bold text-black">CHF {priceInfo.basePrice}</span>
                        </div>
                      )}

                      {/* Sous-total */}
                      <div className="flex justify-between items-center bg-white px-4 py-2 rounded-lg border-t-2 border-gray-200 mt-2">
                        <span className="text-gray-700 font-semibold text-sm">Sous-total</span>
                        <span className="font-bold text-black">CHF {priceInfo.basePrice}</span>
                      </div>

                      {/* Rabais appliqué */}
                      {priceInfo.discount && (
                        <div className="flex justify-between items-center bg-green-50 px-4 py-2 rounded-lg border border-green-200">
                          <span className="text-green-700 text-sm font-semibold flex items-center gap-1">
                            <Percent className="w-3.5 h-3.5" />
                            Rabais ({priceInfo.discount.discountPercent}%)
                          </span>
                          <span className="font-bold text-green-700">- CHF {priceInfo.discountAmount}</span>
                        </div>
                      )}

                      {/* Total */}
                      <div className="flex justify-between items-center bg-yellow-50 px-4 py-3 rounded-lg border-2 border-yellow-400 mt-2">
                        <span className="text-black font-bold">Total à payer</span>
                        <span className="font-bold text-black text-xl">CHF {priceInfo.finalPrice}</span>
                      </div>

                      {/* Kilométrage (valeurs configurées du véhicule + bonus rabais) */}
                      <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
                        {vehicle.includedKm && vehicle.includedKm > 0 ? (
                          <>
                            <div className="flex justify-between items-center bg-blue-50 px-4 py-2.5 rounded-lg border border-blue-200">
                              <div className="flex items-center gap-2">
                                <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                                <span className="text-gray-700 text-sm font-medium">Km inclus / jour</span>
                              </div>
                              <span className="font-bold text-blue-700">
                                {priceInfo.baseKmPerDay} km
                              </span>
                            </div>
                            {priceInfo.bonusKmPerDay > 0 && (
                              <div className="flex justify-between items-center bg-green-50 px-4 py-2.5 rounded-lg border border-green-300">
                                <div className="flex items-center gap-2">
                                  <Sparkles className="w-4 h-4 text-green-600" />
                                  <span className="text-green-700 text-sm font-semibold">Bonus km (+{priceInfo.boostedPercent}%)</span>
                                </div>
                                <span className="font-bold text-green-700">+ {priceInfo.bonusKmPerDay} km / jour</span>
                              </div>
                            )}
                            {priceInfo.bonusKmPerDay > 0 && (
                              <div className="flex justify-between items-center bg-yellow-50 px-4 py-2.5 rounded-lg border-2 border-yellow-400">
                                <span className="text-black text-sm font-bold">Total km inclus</span>
                                <span className="font-bold text-black">
                                  {priceInfo.totalKmPerDay} km / jour ({priceInfo.totalKmIncluded} km au total)
                                </span>
                              </div>
                            )}
                            {vehicle.extraKmPrice > 0 && (
                              <div className="flex justify-between items-center bg-orange-50 px-4 py-2.5 rounded-lg border border-orange-200">
                                <div className="flex items-center gap-2">
                                  <svg className="w-4 h-4 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                                  <span className="text-gray-700 text-sm font-medium">Km supplémentaire</span>
                                </div>
                                <span className="font-bold text-orange-700">CHF {vehicle.extraKmPrice.toFixed(2)} / km</span>
                              </div>
                            )}

                            {/* Simulateur de km */}
                            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg border-2 border-indigo-200 p-4 mt-2">
                              <div className="flex items-center gap-2 mb-3">
                                <div className="bg-indigo-500 p-1.5 rounded-lg">
                                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" /></svg>
                                </div>
                                <h4 className="font-bold text-indigo-900 text-sm">Simulateur de kilométrage</h4>
                              </div>
                              <label className="block text-xs font-semibold text-indigo-800 mb-2">
                                Combien de km prévoyez-vous de faire ?
                              </label>
                              <div className="flex items-center gap-2 mb-3">
                                <button
                                  type="button"
                                  onClick={() => setPlannedKm(Math.max(0, plannedKm - 50))}
                                  className="w-10 h-10 rounded-lg bg-white hover:bg-indigo-100 border border-indigo-300 flex items-center justify-center text-lg font-bold text-indigo-700 transition-colors"
                                >
                                  −
                                </button>
                                <div className="flex-1 relative">
                                  <input
                                    type="number"
                                    min={0}
                                    step={10}
                                    value={plannedKm || ''}
                                    onChange={(e) => setPlannedKm(Math.max(0, parseInt(e.target.value) || 0))}
                                    placeholder="0"
                                    className="w-full bg-white border-2 border-indigo-300 rounded-lg px-3 py-2 text-center text-lg font-bold text-indigo-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                  />
                                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-indigo-500 text-sm font-semibold pointer-events-none">km</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setPlannedKm(plannedKm + 50)}
                                  className="w-10 h-10 rounded-lg bg-white hover:bg-indigo-100 border border-indigo-300 flex items-center justify-center text-lg font-bold text-indigo-700 transition-colors"
                                >
                                  +
                                </button>
                              </div>

                              {/* Résultat du simulateur */}
                              {plannedKm > 0 && (
                                <div className="space-y-2">
                                  <div className="flex justify-between items-center bg-white px-3 py-2 rounded-lg text-xs">
                                    <span className="text-gray-600">Km inclus dans la location</span>
                                    <span className="font-bold text-blue-700">{priceInfo.totalKmIncluded} km</span>
                                  </div>
                                  <div className="flex justify-between items-center bg-white px-3 py-2 rounded-lg text-xs">
                                    <span className="text-gray-600">Km prévus</span>
                                    <span className="font-bold text-indigo-700">{plannedKm} km</span>
                                  </div>
                                  {priceInfo.extraKm > 0 ? (
                                    <>
                                      <div className="flex justify-between items-center bg-orange-100 px-3 py-2 rounded-lg text-xs border border-orange-300">
                                        <span className="text-orange-800 font-semibold">Km supplémentaires</span>
                                        <span className="font-bold text-orange-800">{priceInfo.extraKm} km</span>
                                      </div>
                                      <div className="flex justify-between items-center bg-orange-50 px-3 py-2 rounded-lg text-xs">
                                        <span className="text-gray-700">{priceInfo.extraKm} km × CHF {priceInfo.extraKmPrice.toFixed(2)}</span>
                                        <span className="font-bold text-orange-700">+ CHF {priceInfo.extraKmCost.toFixed(2)}</span>
                                      </div>
                                    </>
                                  ) : (
                                    <div className="bg-green-50 border border-green-300 px-3 py-2 rounded-lg text-xs text-green-800 font-semibold text-center">
                                      ✓ Vous êtes dans le forfait inclus, aucun supplément
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </>
                        ) : (
                          <div className="flex justify-between items-center bg-emerald-50 px-4 py-2.5 rounded-lg border border-emerald-200">
                            <span className="text-gray-700 text-sm font-medium">Kilométrage</span>
                            <span className="font-bold text-emerald-700">Illimité</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right: Booking Form */}
          <div>
            <form onSubmit={handleSubmit} className="bg-gradient-to-br from-white via-yellow-50/30 to-white rounded-2xl shadow-2xl p-8 border-2 border-yellow-400/20">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-gradient-to-br from-yellow-400 to-amber-500 p-3 rounded-xl shadow-lg">
                  <Calendar className="w-6 h-6 text-black" />
                </div>
                <h2 className="text-2xl font-bold bg-gradient-to-r from-black to-gray-700 bg-clip-text text-transparent">{isVehicleUnavailable ? 'Vehicule temporairement indisponible' : 'Reserver ce vehicule'}</h2>
              </div>

              {isVehicleUnavailable && (
                <div className="bg-gradient-to-r from-orange-50 to-amber-50 text-orange-800 px-5 py-5 rounded-xl mb-6 border-l-4 border-orange-500 shadow-md">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-orange-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-bold text-lg mb-1">Vehicule temporairement indisponible</div>
                      <p className="text-sm text-orange-700">Ce vehicule est actuellement indisponible a la reservation (maintenance, preparation, etc.). Vous pouvez consulter ses details mais la reservation n'est pas possible pour le moment.</p>
                      <button
                        type="button"
                        onClick={() => navigate('/flotte')}
                        className="mt-3 inline-flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-lg font-bold text-sm hover:bg-orange-600 transition-colors"
                      >
                        Voir les vehicules disponibles
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-gradient-to-r from-red-50 to-red-100 text-red-700 px-5 py-4 rounded-xl mb-6 border-l-4 border-red-500 shadow-md">
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    {error}
                  </div>
                </div>
              )}

              {/* Section Periode */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 mb-6 border border-blue-200 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-bold text-black">Periode de location</h3>
                </div>

                {/* Date de prise en charge - Calendrier */}
                <div className="mb-5">
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-yellow-600" />
                    Date de prise en charge
                  </label>

                  {loadingDates ? (
                    <div className="flex items-center justify-center py-8 bg-white rounded-xl border-2 border-gray-200">
                      <div className="w-6 h-6 border-3 border-yellow-400 border-t-transparent rounded-full animate-spin mr-3" />
                      <span className="text-gray-500">Chargement des disponibilites...</span>
                    </div>
                  ) : (
                    <>
                    {/* Bandeau d'information : aujourd'hui est fully bloqué (toutes les periodes sont passees) */}
                    {areAllTodayPeriodsPassed() && (
                      <div className="mb-3 bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-orange-300 rounded-xl px-4 py-3 flex items-start gap-3 shadow-sm">
                        <div className="bg-orange-500 p-1.5 rounded-lg flex-shrink-0">
                          <Clock className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="font-bold text-orange-900 text-sm mb-0.5">Aujourd’hui : réservation en ligne clôturée</div>
                          <div className="text-orange-800 text-xs leading-relaxed">
                            Toutes les périodes d’aujourd’hui (matin 8h, après-midi 13h) sont déjà passées. Veuillez choisir une date à partir de demain, ou contacter le garage via WhatsApp pour une prise en charge exceptionnelle.
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="bg-white rounded-xl border-2 border-gray-200 shadow-sm overflow-hidden">
                      {/* Header du calendrier */}
                      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-gray-900 to-black">
                        <button
                          type="button"
                          onClick={prevMonth}
                          className="p-2 rounded-lg hover:bg-white/10 transition-colors text-white"
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </button>
                        <h4 className="font-bold text-white text-lg">
                          {monthNames[calendarMonth.month]} {calendarMonth.year}
                        </h4>
                        <button
                          type="button"
                          onClick={nextMonth}
                          className="p-2 rounded-lg hover:bg-white/10 transition-colors text-white"
                        >
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </div>

                      {/* Jours de la semaine */}
                      <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
                        {dayNames.map((d) => (
                          <div key={d} className="text-center py-2 text-xs font-bold text-gray-500 uppercase">
                            {d}
                          </div>
                        ))}
                      </div>

                      {/* Grille des jours */}
                      <div className="grid grid-cols-7 gap-px bg-gray-100 p-1">
                        {calendarDays.map((day, idx) => {
                          if (!day.date) {
                            return <div key={`empty-${idx}`} className="bg-white p-2" />
                          }

                          const isDisabled = day.isPast || day.isBooked
                          const isSelected = day.isSelected
                          const inRange = day.inRange && !day.isBooked

                          const isDisabledFinal = isDisabled || day.isSunday

                          return (
                            <button
                              key={day.date}
                              type="button"
                              disabled={isDisabledFinal}
                              onClick={() => handleDateSelect(day.date)}
                              className={`relative p-2 text-center rounded-lg transition-all text-sm font-medium ${
                                isSelected
                                  ? 'bg-yellow-400 text-black font-bold shadow-lg scale-110 z-10 ring-2 ring-yellow-500'
                                  : inRange
                                    ? 'bg-yellow-100 text-yellow-800 font-semibold'
                                    : day.isBooked
                                      ? 'bg-red-100 text-red-400 cursor-not-allowed line-through'
                                      : day.isSunday
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : day.isPast
                                          ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
                                          : day.isToday
                                            ? 'bg-blue-50 text-blue-700 font-bold border-2 border-blue-300 hover:bg-blue-100'
                                            : 'bg-white text-gray-700 hover:bg-yellow-50 hover:text-yellow-700 hover:shadow-sm'
                              }`}
                              title={
                                day.isBooked
                                  ? 'Date deja reservee'
                                  : day.isSunday
                                    ? 'Dimanche : garage ferme (pas de prise/retour)'
                                    : day.isToday && areAllTodayPeriodsPassed()
                                      ? 'Aujourd’hui : toutes les périodes sont déjà passées'
                                      : day.isPast
                                        ? 'Date passee'
                                        : `Selectionner le ${day.day}`
                              }
                            >
                              {day.day}
                              {day.isBooked && (
                                <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full" />
                              )}
                              {day.isSunday && !day.isBooked && (
                                <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 text-[9px] font-bold text-gray-500">Dim</span>
                              )}
                              {day.isToday && !isSelected && !day.isSunday && (
                                <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 bg-blue-500 rounded-full" />
                              )}
                            </button>
                          )
                        })}
                      </div>

                      {/* Legende */}
                      <div className="flex items-center gap-4 px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs">
                        <div className="flex items-center gap-1.5">
                          <span className="w-3 h-3 rounded bg-yellow-400 shadow-sm" />
                          <span className="text-gray-600">Selectionne</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="w-3 h-3 rounded bg-red-100 border border-red-300 relative">
                            <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-red-500 rounded-full" />
                          </span>
                          <span className="text-gray-600">Reserve</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="w-3 h-3 rounded bg-blue-50 border-2 border-blue-300" />
                          <span className="text-gray-600">Aujourd'hui</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="w-3 h-3 rounded bg-gray-100 border border-gray-300" />
                          <span className="text-gray-600">Dimanche (ferme)</span>
                        </div>
                        {duration === 'journee' && numberOfDays > 1 && (
                          <div className="flex items-center gap-1.5">
                            <span className="w-3 h-3 rounded bg-yellow-100" />
                            <span className="text-gray-600">Votre periode</span>
                          </div>
                        )}
                      </div>
                    </div>
                    </>
                  )}

                  {/* Alerte si conflit de dates */}
                  {hasConflict && pickupDate && (
                    <div className="mt-3 bg-red-50 border border-red-300 rounded-xl px-4 py-3 flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold text-red-700 text-sm">Conflit de dates</div>
                        <div className="text-red-600 text-xs mt-0.5">
                          Certaines dates de votre periode sont deja reservees. Veuillez choisir une autre date ou reduire le nombre de jours.
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Type de duree */}
                <div className="mb-5">
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Type de location
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => { setDuration('demi-journee'); setNumberOfDays(1) }}
                      className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                        duration === 'demi-journee'
                          ? 'border-yellow-400 bg-yellow-50 shadow-lg scale-105'
                          : 'border-gray-200 bg-white hover:border-yellow-300 hover:shadow-md'
                      }`}
                    >
                      <Sun className="w-8 h-8 text-yellow-500" />
                      <span className="font-bold text-black text-sm">Demi-journee</span>
                      <span className="text-xs text-gray-500">Matin ou apres-midi</span>
                      <span className="text-lg font-bold text-yellow-600">CHF {vehicle.pricePerHalfDay || Math.round((vehicle.pricePerDay || 0) / 2)}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setDuration('journee')}
                      className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                        duration === 'journee'
                          ? 'border-yellow-400 bg-yellow-50 shadow-lg scale-105'
                          : 'border-gray-200 bg-white hover:border-yellow-300 hover:shadow-md'
                      }`}
                    >
                      <Sunset className="w-8 h-8 text-orange-500" />
                      <span className="font-bold text-black text-sm">Journee</span>
                      <span className="text-xs text-gray-500">8h00 - 17h00</span>
                      <span className="text-lg font-bold text-yellow-600">CHF {vehicle.pricePerDay}</span>
                    </button>
                  </div>
                </div>

                {/* Choix matin / apres-midi (si demi-journee) */}
                {duration === 'demi-journee' && (
                  <div className="mb-5">
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Créneau horaire
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setHalfDaySlot('matin')}
                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                          halfDaySlot === 'matin'
                            ? 'border-yellow-400 bg-yellow-50 shadow-lg scale-105'
                            : 'border-gray-200 bg-white hover:border-yellow-300 hover:shadow-md'
                        }`}
                      >
                        <Sun className="w-7 h-7 text-yellow-500" />
                        <span className="font-bold text-black text-sm">Matin</span>
                        <span className="text-xs text-gray-500">8h00 - 12h00</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setHalfDaySlot('apres-midi')}
                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                          halfDaySlot === 'apres-midi'
                            ? 'border-orange-400 bg-orange-50 shadow-lg scale-105'
                            : 'border-gray-200 bg-white hover:border-orange-300 hover:shadow-md'
                        }`}
                      >
                        <Sunset className="w-7 h-7 text-orange-500" />
                        <span className="font-bold text-black text-sm">Apres-midi</span>
                        <span className="text-xs text-gray-500">13h00 - 17h00</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Nombre de jours (si journee) */}
                {duration === 'journee' && (
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Nombre de journees
                    </label>
                    {!pickupDate && (
                      <div className="mb-3 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2 flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div className="text-xs text-amber-800">
                          Selectionnez d'abord une <strong>date de prise en charge</strong> dans le calendrier pour pouvoir ajouter des journees.
                        </div>
                      </div>
                    )}
                    <div className={`flex items-center gap-4 ${!pickupDate ? 'opacity-50' : ''}`}>
                      <button
                        type="button"
                        disabled={!pickupDate}
                        onClick={() => setNumberOfDays(Math.max(1, numberOfDays - 1))}
                        className="w-12 h-12 rounded-xl bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-2xl font-bold transition-colors disabled:cursor-not-allowed disabled:hover:bg-gray-200"
                        title={!pickupDate ? 'Selectionnez d\'abord une date' : 'Retirer une journee'}
                      >
                        -
                      </button>
                      <div className="flex-1 text-center">
                        <div className="text-4xl font-bold text-black">{numberOfDays}</div>
                        <div className="text-sm text-gray-500">{numberOfDays === 1 ? 'journee' : 'journees'}</div>
                      </div>
                      <button
                        type="button"
                        disabled={!pickupDate || wouldNextDayConflict}
                        onClick={handleIncrementDays}
                        className="w-12 h-12 rounded-xl bg-yellow-400 hover:bg-yellow-500 flex items-center justify-center text-2xl font-bold transition-colors disabled:cursor-not-allowed disabled:hover:bg-yellow-400 disabled:bg-gray-300 disabled:text-gray-500"
                        title={!pickupDate ? 'Selectionnez d\'abord une date' : wouldNextDayConflict ? 'Impossible : la prochaine journee est deja reservee' : 'Ajouter une journee'}
                      >
                        +
                      </button>
                    </div>
                    {addDayToast && (
                      <div className="mt-3 bg-red-50 border border-red-300 rounded-lg px-3 py-2 flex items-start gap-2 animate-pulse">
                        <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                        <div className="text-xs text-red-700">
                          {addDayToast}
                        </div>
                      </div>
                    )}
                    {numberOfDays >= 7 && (
                      <div className="mt-3 bg-green-50 text-green-700 px-4 py-2 rounded-lg text-sm font-medium border border-green-200">
                        Tarif semaine (7 jours) applique !
                      </div>
                    )}
                    {/* Indicateur de prochain rabais */}
                    {duration === 'journee' && (() => {
                      const currentDiscount = getApplicableDiscount(numberOfDays)
                      const nextDiscount = discountRules
                        .filter((r) => r.active !== false && r.minDays > numberOfDays)
                        .sort((a, b) => a.minDays - b.minDays)[0]
                      return (
                        <>
                          {currentDiscount && (
                            <div className="mt-3 bg-green-50 text-green-700 px-4 py-2 rounded-lg text-sm font-medium border border-green-200 flex items-center gap-2">
                              <Percent className="w-4 h-4" />
                              Rabais de {currentDiscount.discountPercent}% applique ({currentDiscount.label})
                            </div>
                          )}
                          {nextDiscount && (
                            <div className="mt-2 bg-blue-50 text-blue-600 px-4 py-2 rounded-lg text-xs font-medium border border-blue-200">
                              Ajoutez {nextDiscount.minDays - numberOfDays} jour{nextDiscount.minDays - numberOfDays > 1 ? 's' : ''} pour beneficier de -{nextDiscount.discountPercent}% !
                            </div>
                          )}
                        </>
                      )
                    })()}
                  </div>
                )}

                {/* Resume des dates */}
                {pickupDate && (
                  <div className="mt-4 bg-white rounded-xl p-4 border border-blue-100 shadow-sm">
                    <div className="text-sm font-semibold text-gray-500 mb-2 uppercase tracking-wide">Resume</div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        <div>
                          <div className="text-xs text-gray-500">Prise en charge</div>
                          <div className="font-semibold text-black text-sm">{formatDateTime(pickupDt)}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                        <div>
                          <div className="text-xs text-gray-500">Retour</div>
                          <div className="font-semibold text-black text-sm">{formatDateTime(returnDt)}</div>
                        </div>
                      </div>
                      <div className="pt-2 border-t border-gray-100">
                        <div className="text-sm text-gray-600 flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          {getDurationLabel()}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Section Informations client */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 mb-6 border border-green-200 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="w-5 h-5 text-green-600" />
                  <h3 className="text-lg font-bold text-black">Vos informations</h3>
                </div>
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="Nom complet"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 bg-white shadow-sm transition-all placeholder:text-gray-400"
                    required
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={clientEmail}
                    onChange={(e) => setClientEmail(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 bg-white shadow-sm transition-all placeholder:text-gray-400"
                    required
                  />
                  <input
                    type="tel"
                    placeholder="Telephone (ex: +41 79 123 45 67)"
                    value={clientPhone}
                    onChange={(e) => setClientPhone(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 bg-white shadow-sm transition-all placeholder:text-gray-400"
                    required
                  />
                  <input
                    type="text"
                    placeholder="Adresse complete"
                    value={clientAddress}
                    onChange={(e) => setClientAddress(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 bg-white shadow-sm transition-all placeholder:text-gray-400"
                    required
                  />
                </div>
              </div>

              {/* Recapitulatif prix */}
              <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 rounded-2xl p-6 mb-6 shadow-2xl border-2 border-yellow-400">
                <div className="flex items-center gap-2 mb-4">
                  <CreditCard className="w-5 h-5 text-yellow-400" />
                  <h3 className="text-lg font-bold text-white">Recapitulatif</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center pb-3 border-b border-gray-700">
                    <span className="text-gray-300">
                      {duration === 'demi-journee' ? `1 demi-journee ${halfDaySlot === 'matin' ? 'matin' : 'apres-midi'}` : `${priceInfo.billedDays} journee${priceInfo.billedDays > 1 ? 's' : ''} facturée${priceInfo.billedDays > 1 ? 's' : ''}`}
                    </span>
                    <span className={`font-bold text-lg ${priceInfo.discount ? 'text-gray-400 line-through' : 'text-white'}`}>
                      CHF {priceInfo.basePrice}
                    </span>
                  </div>

                  {/* Info lundi supplémentaire facturé (retour dimanche reporté au lundi) */}
                  {extraMondayDay > 0 && (
                    <div className="flex justify-between items-center pb-3 border-b border-gray-700">
                      <div className="flex items-center gap-2">
                        <div className="bg-amber-500/20 p-1.5 rounded-lg">
                          <Calendar className="w-4 h-4 text-amber-400" />
                        </div>
                        <div>
                          <span className="text-amber-300 font-semibold text-sm">
                            +1 jour (lundi de retour)
                          </span>
                          <span className="text-gray-400 text-xs ml-2 block">Retour dimanche reporté au lundi → jour payant</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Info dimanches inclus (comptés comme jours payants) */}
                  {priceInfo.sundaysIncluded > 0 && (
                    <div className="flex justify-between items-center pb-3 border-b border-gray-700">
                      <div className="flex items-center gap-2">
                        <div className="bg-blue-500/20 p-1.5 rounded-lg">
                          <Calendar className="w-4 h-4 text-blue-400" />
                        </div>
                        <div>
                          <span className="text-blue-300 font-semibold text-sm">
                            {priceInfo.sundaysIncluded} dimanche{priceInfo.sundaysIncluded > 1 ? 's' : ''} inclus
                          </span>
                          <span className="text-gray-400 text-xs ml-2 block">Compté{priceInfo.sundaysIncluded > 1 ? 's' : ''} comme jour{priceInfo.sundaysIncluded > 1 ? 's' : ''} payant{priceInfo.sundaysIncluded > 1 ? 's' : ''}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Rabais applique */}
                  {priceInfo.discount && (
                    <div className="flex justify-between items-center pb-3 border-b border-gray-700">
                      <div className="flex items-center gap-2">
                        <div className="bg-green-500/20 p-1.5 rounded-lg">
                          <Percent className="w-4 h-4 text-green-400" />
                        </div>
                        <div>
                          <span className="text-green-400 font-semibold">Rabais {priceInfo.discount.label}</span>
                          <span className="text-green-500 text-sm ml-2">-{priceInfo.discount.discountPercent}%</span>
                        </div>
                      </div>
                      <span className="font-bold text-green-400 text-lg">- CHF {priceInfo.discountAmount}</span>
                    </div>
                  )}

                  {/* Km supplémentaires facturés */}
                  {priceInfo.extraKm > 0 && (
                    <div className="flex justify-between items-center pb-3 border-b border-gray-700">
                      <div className="flex items-center gap-2">
                        <div className="bg-orange-500/20 p-1.5 rounded-lg">
                          <svg className="w-4 h-4 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                        </div>
                        <div>
                          <span className="text-orange-400 font-semibold">+ {priceInfo.extraKm} km suppl.</span>
                          <span className="text-orange-500 text-sm ml-2">(CHF {priceInfo.extraKmPrice.toFixed(2)}/km)</span>
                        </div>
                      </div>
                      <span className="font-bold text-orange-400 text-lg">+ CHF {priceInfo.extraKmCost.toFixed(2)}</span>
                    </div>
                  )}

                  <div className="flex justify-between items-center pt-2">
                    <span className="text-xl font-bold text-white">Total:</span>
                    <div className="text-right">
                      <div className="text-3xl font-bold bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-500 bg-clip-text text-transparent">CHF {totalPrice}</div>
                      {priceInfo.discount && (
                        <div className="text-xs text-green-400 mt-0.5 font-semibold">Vous economisez CHF {priceInfo.discountAmount}</div>
                      )}
                      <div className="text-xs text-gray-400 mt-1">+ Caution CHF {vehicle.deposit}</div>
                    </div>
                  </div>
                  {/* Info kilométrage (avec bonus rabais) */}
                  {vehicle.includedKm > 0 && (
                    <div className="mt-4 pt-3 border-t border-gray-700">
                      <div className="flex items-start gap-2 text-sm">
                        <svg className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        <div className="text-gray-300">
                          <span className="font-semibold text-white">{priceInfo.totalKmIncluded} km inclus</span>
                          <span className="text-gray-400"> ({priceInfo.totalKmPerDay} km/jour)</span>
                          {vehicle.extraKmPrice > 0 && (
                            <span className="text-orange-400"> • CHF {vehicle.extraKmPrice.toFixed(2)}/km suppl.</span>
                          )}
                        </div>
                      </div>
                      {priceInfo.bonusKmPerDay > 0 && (
                        <div className="mt-2 bg-green-500/10 border border-green-500/30 rounded-lg px-3 py-2 flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-green-400 flex-shrink-0" />
                          <div className="text-xs text-green-300">
                            <span className="font-bold">Bonus km +{priceInfo.boostedPercent}%</span>
                            <span className="text-green-400/80"> • +{priceInfo.bonusKmPerDay} km/jour offerts avec votre rabais</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {(!vehicle.includedKm || vehicle.includedKm === 0) && (
                    <div className="mt-4 pt-3 border-t border-gray-700">
                      <div className="flex items-center gap-2 text-sm text-emerald-400">
                        <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                        <span className="font-semibold">Kilométrage illimité</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Alerte dimanche : garage ferme */}
              {isPickupSunday && (
                <div className="mb-4 bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-orange-300 rounded-xl px-4 py-4 shadow-sm">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="bg-orange-500 p-2 rounded-lg flex-shrink-0">
                      <AlertTriangle className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="font-bold text-orange-900 text-sm mb-1">
                        Dimanche : garage ferme
                      </div>
                      <div className="text-orange-800 text-xs leading-relaxed">
                        Le garage est ferme le dimanche : <strong>la prise et le retour de vehicule ne sont pas possibles ce jour-la</strong>.
                        Les dimanches sont toutefois <strong>comptes comme jours payants</strong> lorsqu'ils sont inclus dans une location en cours (ex : location du vendredi au lundi).
                        Veuillez selectionner un autre jour pour la prise du vehicule, ou contacter le garage via WhatsApp pour une demande particuliere.
                      </div>
                    </div>
                  </div>
                  <a
                    href={whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white py-3 rounded-xl font-bold text-sm shadow-lg hover:shadow-xl transition-all hover:scale-[1.02]"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Contacter le garage via WhatsApp
                  </a>
                </div>
              )}

              {/* Alerte samedi apres-midi bloque */}
              {isSaturdayAfternoonBlocked && (
                <div className="mb-4 bg-red-50 border border-red-300 rounded-xl px-4 py-3 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-red-700 text-sm">Creneau indisponible</div>
                    <div className="text-red-600 text-xs mt-0.5">
                      La reservation du samedi apres-midi n'est plus possible apres 11h00 le jour meme. Veuillez choisir une autre date ou contacter le garage.
                    </div>
                  </div>
                </div>
              )}

              {/* Alerte periode deja demarree : proposer contact WhatsApp */}
              {periodStartPassed && !isSaturdayAfternoonBlocked && (
                <div className="mb-4 bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-orange-300 rounded-xl px-4 py-4 shadow-sm">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="bg-orange-500 p-2 rounded-lg flex-shrink-0">
                      <Clock className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="font-bold text-orange-900 text-sm mb-1">
                        La periode a deja demarre
                      </div>
                      <div className="text-orange-800 text-xs leading-relaxed">
                        Le creneau choisi commence a {duration === 'demi-journee' && halfDaySlot === 'apres-midi' ? '13h00' : '08h00'} et il est deja passe.
                        La reservation en ligne n'est plus possible pour ce creneau aujourd'hui.
                        Vous pouvez toutefois contacter directement le garage via WhatsApp pour demander si la prise du vehicule est encore possible.
                      </div>
                    </div>
                  </div>
                  <a
                    href={whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white py-3 rounded-xl font-bold text-sm shadow-lg hover:shadow-xl transition-all hover:scale-[1.02]"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Contacter le garage via WhatsApp
                  </a>
                  <p className="text-center text-orange-700 text-xs mt-2">
                    Message pre-rempli avec les details de votre demande
                  </p>
                </div>
              )}

              {/* Info retour dimanche decale au lundi (dimanche reste compte comme jour paye) */}
              {returnDt && !isPickupSunday && (() => {
                const pickupD = pickupDt ? new Date(pickupDt) : null
                if (!pickupD) return null
                // Calcul du retour theorique avant ajustement
                let theoreticalReturn: Date
                if (duration === 'demi-journee') {
                  theoreticalReturn = new Date(`${pickupDate}T${halfDaySlot === 'matin' ? '12' : '17'}:00:00`)
                } else {
                  const end = new Date(pickupDate)
                  end.setDate(end.getDate() + numberOfDays - 1)
                  const y = end.getFullYear()
                  const m = String(end.getMonth() + 1).padStart(2, '0')
                  const d = String(end.getDate()).padStart(2, '0')
                  theoreticalReturn = new Date(`${y}-${m}-${d}T17:00:00`)
                }
                if (theoreticalReturn.getDay() === 0) {
                  return (
                    <div className="mb-4 bg-blue-50 border border-blue-300 rounded-xl px-4 py-3 flex items-start gap-3">
                      <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold text-blue-700 text-sm">Retour reporte au lundi</div>
                        <div className="text-blue-600 text-xs mt-0.5">
                          Le garage est ferme le dimanche : votre retour est automatiquement reporte au lundi suivant. Le dimanche reste compte comme jour paye dans la duree totale de votre location.
                        </div>
                      </div>
                    </div>
                  )
                }
                return null
              })()}

              {/* Panneau explicatif : raisons pour lesquelles le bouton de confirmation est grise */}
              {(() => {
                const reasons: { icon: JSX.Element; label: string }[] = []
                if (!pickupDate) reasons.push({ icon: <Calendar className="w-4 h-4" />, label: 'Aucune date de prise en charge selectionnee' })
                if (hasConflict) reasons.push({ icon: <AlertTriangle className="w-4 h-4" />, label: 'Conflit de dates : certaines journees sont deja reservees' })
                if (isVehicleUnavailable) reasons.push({ icon: <Shield className="w-4 h-4" />, label: 'Vehicule temporairement indisponible (maintenance ou preparation)' })
                if (isPickupSunday) reasons.push({ icon: <Clock className="w-4 h-4" />, label: 'Le garage est ferme le dimanche : prise du vehicule impossible' })
                if (isSaturdayAfternoonBlocked) reasons.push({ icon: <Clock className="w-4 h-4" />, label: 'Samedi apres-midi bloque (apres 11h00 le jour meme)' })
                if (periodStartPassed) reasons.push({ icon: <Clock className="w-4 h-4" />, label: 'La periode choisie a deja demarre aujourd\'hui' })
                if (reasons.length === 0) return null
                return (
                  <div className="mb-4 bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 rounded-xl px-4 py-3 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="bg-amber-500 p-1.5 rounded-lg">
                        <AlertTriangle className="w-4 h-4 text-white" />
                      </div>
                      <div className="font-bold text-amber-900 text-sm">
                        Reservation impossible pour le moment
                      </div>
                    </div>
                    <div className="text-xs text-amber-800 mb-2">
                      Merci de corriger le ou les points suivants pour pouvoir confirmer :
                    </div>
                    <ul className="space-y-1.5">
                      {reasons.map((r, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-amber-900 bg-white/60 rounded-lg px-2.5 py-1.5">
                          <span className="text-amber-600 flex-shrink-0 mt-0.5">{r.icon}</span>
                          <span className="flex-1">{r.label}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              })()}

              <button
                type="submit"
                disabled={submitting || hasConflict || !pickupDate || isVehicleUnavailable || isSaturdayAfternoonBlocked || periodStartPassed || isPickupSunday}
                className="w-full bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-400 text-black py-4 rounded-xl font-bold text-lg hover:shadow-2xl hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all duration-300 shadow-lg border-2 border-yellow-600"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-3 border-black border-t-transparent rounded-full animate-spin" />
                    Traitement en cours...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Check className="w-6 h-6" />
                    {isVehicleUnavailable ? 'Reservation impossible' : isPickupSunday ? 'Dimanche : garage ferme' : isSaturdayAfternoonBlocked ? 'Creneau indisponible' : periodStartPassed ? 'Periode deja demarree — contactez-nous' : 'Confirmer la reservation'}
                  </span>
                )}
              </button>

              <div className="mt-6 bg-gradient-to-r from-gray-100 to-gray-50 rounded-xl p-4 border border-gray-200">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-black mb-1">Lieu de retrait et retour</div>
                    <div className="text-sm text-gray-600">Rue de l'Industrie 49, 1030 Bussigny</div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Modal de confirmation : ajout d'un jour tombe un dimanche */}
      {sundayConfirmModal.open && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSundayConfirmModal({ open: false, targetDays: 0 })}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 border-4 border-yellow-400"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="bg-yellow-400 p-2 rounded-xl flex-shrink-0">
                <Calendar className="w-6 h-6 text-black" />
              </div>
              <div>
                <h3 className="text-xl font-black text-black mb-1">Retour un dimanche</h3>
                <p className="text-sm text-gray-600">Le garage est ferme le dimanche</p>
              </div>
            </div>

            <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-gray-800 leading-relaxed">
                En ajoutant ce jour, votre retour tombera un <strong>dimanche</strong>.
                Comme le garage est ferme ce jour-la, le retour sera automatiquement
                reporte au <strong>lundi</strong>.
              </p>
              <p className="text-sm text-gray-800 leading-relaxed mt-2">
                Cela ajoute <strong>2 jours supplementaires</strong> a votre location
                (le dimanche + le lundi de retour, tous deux comptes comme jours payants).
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => setSundayConfirmModal({ open: false, targetDays: 0 })}
                className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl font-bold transition-colors"
              >
                Refuser
              </button>
              <button
                type="button"
                onClick={() => {
                  setNumberOfDays(sundayConfirmModal.targetDays)
                  setSundayConfirmModal({ open: false, targetDays: 0 })
                }}
                className="flex-1 px-4 py-3 bg-yellow-400 hover:bg-yellow-500 text-black rounded-xl font-bold transition-colors shadow-lg"
              >
                Accepter (+2 jours)
              </button>
            </div>
          </div>
        </div>
      )}

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        mode="login"
      />
    </div>
  )
}
